import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionItem,
  Button,
  Checkbox,
  Dropdown,
  InlineLoading,
  InlineNotification,
  Tag,
  TextInput,
} from '@carbon/react';
import { Tearsheet } from '@carbon/ibm-products';
import { Add, CollapseAll, ExpandAll, SubtractAlt } from '@carbon/react/icons';
import { DataTable } from 'components/data-table';
import type {
  DataTableColumnDef,
  DataTableSelectionState,
} from 'components/data-table/types';
import { DatePicker } from 'components/date-picker';
import { FormattedCount } from 'components/formatted-count';
import { StatusDot } from 'components/well-cards';
import { getDateString } from 'utils/dates';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import {
  DATE_FIELD_LABELS,
  EDITABLE_DATE_FIELDS,
  FIELD_TO_SERVER_KEY,
  REQUIRED_DATES_BY_STATUS,
  WELL_STATUS_LABELS,
  WELL_STATUS_ORDER,
  wellStatusLabelOrDash,
  type EditableDateField,
  type ManualWellInput,
  type StagedDateField,
  type WellQueryRow,
  type WellStatusToken,
  type WellWorkspaceDetail,
} from 'types/well-workspace';
import { useManageWells, useWells, type ManageWellsOutcome } from '../hooks';
import { NEW_WELL_PLACEHOLDER, wellDisplayName } from '../utils';
import {
  errorFor,
  validateManualAddStatus,
  type StatusEditValues,
} from '../well-status-validation';
import styles from './manage-wells-tearsheet.module.scss';

// The dates a manual add can carry: the operator-writable lifecycle dates plus first production. A
// strict subset of StagedDateField (no lastProductionDate — always derived), so each is a key of
// both ManualRow and FIELD_TO_SERVER_KEY and needs no cast when indexing either.
type ManualDateField = EditableDateField | 'firstProductionDate';

// Chronological render order, first production last. A row only ever shows the dates its status
// requires, and no status requires rig release or completion — so those two never render.
const MANUAL_DATE_ORDER: ManualDateField[] = [
  ...EDITABLE_DATE_FIELDS,
  'firstProductionDate',
];

interface ManageWellsTearsheetProps {
  open: boolean;
  /** The workspace being edited — its wells are the current membership. */
  workspace: WellWorkspaceDetail;
  onClose: () => void;
}

// A manual row as the form holds it: the Well Name fields, status, and the lifecycle dates
// (camelCase here, to reuse the Story-2 DatePicker and validator; mapped to snake_case at send).
//
// `key` is a stable client id so adding or removing a row never re-keys another row's inputs
// mid-edit. `include` is what the roster's minus toggles: unstaging a manual row UNCHECKS it
// rather than deleting it, so the values already typed survive and can be put back without
// retyping — the mock's behaviour, and the reason this is a flag rather than a splice.
interface ManualRow {
  key: string;
  include: boolean;
  leaseName: string;
  wellNumber: string;
  status: WellStatusToken;
  permitDate: string | null;
  spudDate: string | null;
  rigReleaseDate: string | null;
  completionDate: string | null;
  firstProductionDate: string | null;
}

/**
 * A blank manual row.
 * @param {number} seq Its position, for the row's stable key.
 * @return {ManualRow} The row.
 */
const emptyManualRow = (seq: number): ManualRow => ({
  key: `row-${seq}`,
  include: true,
  leaseName: '',
  wellNumber: '',
  status: 'pre_permit',
  permitDate: null,
  spudDate: null,
  rigReleaseDate: null,
  completionDate: null,
  firstProductionDate: null,
});

/**
 * The dates the chosen status requires, in chronological render order. A dateless stage
 * (Pre-Permit / APD, and the three post-production stages) returns none.
 * @param {WellStatusToken} status The row's chosen status.
 * @return {ManualDateField[]} The date fields to render, validate and send.
 */
const requiredDatesFor = (status: WellStatusToken): ManualDateField[] => {
  const required = new Set<StagedDateField>(
    REQUIRED_DATES_BY_STATUS[status] ?? []
  );

  return MANUAL_DATE_ORDER.filter((field) => required.has(field));
};

/**
 * The row's values for validation, carrying ONLY the dates its status requires. A date left over
 * from a previously-chosen status is neither rendered, sent, nor validated, so switching back to a
 * dateless stage can never strand an ordering error on a field the reader can no longer see.
 * @param {ManualRow} row The manual row.
 * @return {StatusEditValues} The values to validate.
 */
const rowStatusValues = (row: ManualRow): StatusEditValues => {
  const required = new Set(requiredDatesFor(row.status));
  /**
   * @param {ManualDateField} field A date field.
   * @return {string | null} Its value, or null when this status does not require it.
   */
  const pick = (field: ManualDateField): string | null => {
    return required.has(field) ? (row[field] ?? null) : null;
  };

  return {
    status: row.status,
    permitDate: pick('permitDate'),
    spudDate: pick('spudDate'),
    rigReleaseDate: pick('rigReleaseDate'),
    completionDate: pick('completionDate'),
    firstProductionDate: pick('firstProductionDate'),
    lastProductionDate: null,
  };
};

interface CandidateRow extends Record<string, unknown> {
  id: string;
  name: string;
  uwi: string;
  status: string;
  currentOperator: string;
  county: string;
  state: string;
}

// The "add existing" table, in the mock's six columns and its order — Status last, after the
// place columns. All six are already on the wire from queries/wells/create.json.jbuilder.
// Sized explicitly. Left to TanStack's 150px default, six columns plus the checkbox overflow the
// left track and the last one (Status) is reachable only by scrolling sideways — the mock shows
// all six at once. The narrow, predictable columns (UWI is fixed-width, State and County are short
// names, Status is a dot plus a short label) give their slack to Well Name and Operator.
const COLUMNS: DataTableColumnDef<CandidateRow>[] = [
  { accessorKey: 'name', header: 'Well Name', size: 180 },
  { accessorKey: 'uwi', header: 'UWI', size: 110 },
  { accessorKey: 'currentOperator', header: 'Operator', size: 190 },
  { accessorKey: 'county', header: 'County', size: 100 },
  { accessorKey: 'state', header: 'State', size: 90 },
  {
    accessorKey: 'status',
    header: 'Status',
    size: 130,
    cell: ({ getValue }) => (
      <span className={styles.statusCell}>
        <StatusDot status={getValue<string>()} />
      </span>
    ),
  },
];

/**
 * The catalog results as table rows.
 * @param {WellQueryRow[]} wells The page of candidates.
 * @return {CandidateRow[]} The rows.
 */
const toTableRows = (wells: WellQueryRow[]): CandidateRow[] =>
  wells.map((well) => ({
    id: well.uwi,
    name: well.name ?? '—',
    uwi: well.uwi,
    status: well.status ?? '',
    currentOperator: well.currentOperator ?? '—',
    county: well.county ?? '—',
    // The display name ("Texas"), as the mock's State column shows it.
    state: well.stateAbbreviation ?? '—',
  }));

// Why County / State / Formation take no input on a manual row.
const DERIVED_HINT =
  'Derived by the backend from the wellbore geometry once the well has one — not typed here.';

// One line of the "Wells in this Workspace" roster: membership as this Save would leave it.
// `state` drives the two renderings that make a staged edit legible — a member marked for removal
// is struck through and its control reads Undo; a staged add is tagged New and its control takes
// it back out of the batch.
interface RosterRow extends Record<string, unknown> {
  id: string;
  well: string;
  source: 'Manual' | 'Existing Well';
  status: string;
  statusToken: string;
  state: 'member' | 'removing' | 'adding';
  onAction: () => void;
}

const ROSTER_COLUMNS: DataTableColumnDef<RosterRow>[] = [
  {
    accessorKey: 'well',
    header: 'Well',
    size: 170,
    cell: ({ row, getValue }) => (
      <span
        className={
          row.original.state === 'removing'
            ? `${styles.wsName} ${styles.wsNameRemoving}`
            : styles.wsName
        }
      >
        <StatusDot status={row.original.statusToken} showLabel={false} />
        <span className={styles.wsNameText}>{getValue<string>()}</span>
        {row.original.state === 'adding' && (
          <Tag type="green" size="sm">
            New
          </Tag>
        )}
      </span>
    ),
  },
  { accessorKey: 'source', header: 'Source', size: 110 },
  { accessorKey: 'status', header: 'Status', size: 105 },
  {
    id: 'action',
    header: '',
    size: 72,
    enableHiding: false,
    cell: ({ row }) => {
      return row.original.state === 'removing' ? (
        <Button kind="ghost" size="sm" onClick={row.original.onAction}>
          Undo
        </Button>
      ) : (
        <Button
          hasIconOnly
          kind="danger--ghost"
          size="sm"
          iconDescription={
            row.original.state === 'member'
              ? 'Remove from workspace'
              : 'Remove from this batch'
          }
          tooltipPosition="left"
          renderIcon={SubtractAlt}
          onClick={row.original.onAction}
        />
      );
    },
  },
];

// The display name for a staged manual row, matching the SRP Well Name logic (Well.format_name /
// wellDisplayName) the rest of the workspace uses: "LEASE #NUMBER", either half alone, else the same
// "New well" placeholder — not a one-off "Untitled well" or a space-joined format.
//
// This is what the row reads as NOW, not what it will save as. The server completes whichever half
// was left blank — a missing lease becomes "new", a missing number the next in sequence — so a row
// showing "Smith" saves as "Smith #4", and a blank one saves as "new #4". The number is counted
// across the whole workspace and advances within a batch, so the client cannot know it in advance.
/**
 * What a half-finished Save achieved, in one sentence.
 *
 * Counted off what was SENT rather than off the response's `results`, which is the whole
 * post-add membership rather than this call's delta — and the skipped set names existing wells
 * only, since a bad manual row refuses the whole batch instead of being dropped from it.
 * @param {ManageWellsOutcome} outcome - What the Save reported.
 * @return {string} The sentence for the banner.
 */
const partialSummary = (outcome: ManageWellsOutcome): string => {
  const parts: string[] = [];
  const skipped = outcome.skippedUwis.length;
  if (skipped > 0) {
    parts.push(
      // "existing wells", because that is all this counts: a manual row can never be
      // skipped — a bad one refuses the whole batch instead of being dropped from it.
      `Added ${outcome.requestedAdds - skipped} of ${outcome.requestedAdds}` +
        ` existing wells — ${skipped} ${
          skipped === 1 ? 'well no longer exists' : 'wells no longer exist'
        }.`
    );
  }
  if (outcome.failedRemovals.length > 0) {
    const failed = outcome.failedRemovals.length;
    parts.push(
      `${failed} ${failed === 1 ? 'removal' : 'removals'} could not be` +
        ' completed, and ' +
        `${failed === 1 ? 'is' : 'are'} still staged below.`
    );
  }

  return parts.join(' ');
};

/**
 * What the roster calls a manual row before the server has named it.
 * @param {ManualRow} row The row.
 * @return {string} Its display name.
 */
const manualDisplayName = (row: ManualRow): string => {
  const number = row.wellNumber.trim();
  const parts = [row.leaseName.trim(), number && `#${number}`].filter(Boolean);
  return parts.join(' ') || NEW_WELL_PLACEHOLDER;
};

/**
 * The Add or Remove Wells tearsheet (Epic 3, Story 3): one surface to add existing wells from the
 * catalog, add new manual wells, and remove members — staging all three together and committing
 * them in a single Save ("Add 2, Remove 1"). Cancel discards everything.
 *
 * Mirrors the create-workspace modal's search-and-select half for "add existing", so the two feel
 * the same. Only offered on a DRAFT workspace: a promoted one's membership is frozen, which the
 * rail already enforces by withholding the control that opens this.
 * @param {ManageWellsTearsheetProps} props The workspace and the open/close handlers.
 * @return {JSX.Element} The tearsheet.
 */
// eslint-disable-next-line max-lines-per-function -- one sheet wiring the roster, the catalog search and the manual-add table to a single staged set; the three read and write that same state, so splitting them would turn it into prop plumbing.
export function ManageWellsTearsheet({
  open,
  workspace,
  onClose,
}: ManageWellsTearsheetProps) {
  const members = workspace.wells;
  const memberUwis = useMemo(() => members.map((w) => w.uwi), [members]);

  // What is staged, per the three activities. All reset on open and on Cancel. Existing adds are
  // held as a uwi→row map rather than a bare Set so the roster can show each staged add's name and
  // status without re-fetching the page it was picked from.
  const [addRows, setAddRows] = useState<Map<string, CandidateRow>>(new Map());
  const [removeUwis, setRemoveUwis] = useState<Set<string>>(new Set());
  const [manualRows, setManualRows] = useState<ManualRow[]>([]);
  // Monotonic, so a row key is never reused within a session and never depends on Math.random.
  const [rowSeq, setRowSeq] = useState(0);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [search, setSearch] = useState('');
  // Each add-method panel's open state, held here rather than left to the Accordion so the
  // header's Expand all / Collapse all can drive both at once (Carbon's Accordion has no
  // all-items control of its own). Both start open, as the mock draws them.
  const [existingOpen, setExistingOpen] = useState(true);
  const [manualOpen, setManualOpen] = useState(true);

  const { saving, error, errorRow, save, reset } = useManageWells(workspace.id);
  /* What the last Save achieved, when it achieved only part of it. Held so the sheet can stay
     open and say so; cleared on the next attempt and on reopen. */
  const [outcome, setOutcome] = useState<ManageWellsOutcome | null>(null);
  /* The rows this Save SENT, in the order it sent them, so a refusal naming row N can be
     resolved back to a row. Captured at send time on purpose: `details.row` indexes the sent
     array, and by the time the refusal lands the reader may have unchecked a row and shifted
     every index after it. */
  const sentKeysRef = useRef<string[]>([]);

  // Every open starts clean: no staged changes, the search back at its first page, and any prior
  // refusal cleared.
  useEffect(() => {
    if (!open) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- reopening the sheet resets it; `open` is a prop, and keying the whole sheet on it would remount the catalog table and lose its page.
    setAddRows(new Map());
    setRemoveUwis(new Set());
    setManualRows([]);
    setPage(1);
    setSearch('');
    setExistingOpen(true);
    setManualOpen(true);
    setOutcome(null);
    reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `reset` is rebuilt every render by useMutation; keying on `open` alone is intended.
  }, [open]);

  // The search excludes wells already in the workspace, so the table only ever offers wells that
  // are not members yet. Deliberately members ONLY, not members plus this session's staged adds:
  // excluding a staged add would drop its checked row out of the table and leave no way to
  // un-check it. Held while the sheet is closed, so opening it is what sends the read.
  const candidatesQuery = useMemo(
    () => ({
      exclude_uwis: memberUwis,
      ...(search !== '' && { text: search }),
      page,
      pageSize,
      limit: pageSize,
    }),
    [memberUwis, search, page, pageSize]
  );
  const candidatesResult = useWells(candidatesQuery, open);
  const candidates = useMemo(
    () => candidatesResult.data?.rows ?? [],
    [candidatesResult.data]
  );
  const totalCount = candidatesResult.data?.totalCount ?? 0;
  const rows = useMemo(() => toTableRows(candidates), [candidates]);

  const stagedManual = useMemo(
    () => manualRows.filter((row) => row.include),
    [manualRows]
  );

  // Membership as this Save would leave it, as ONE list the shared DataTable can own: current
  // members first, then the session's staged adds. `state` is what the Well and action cells
  // render differently — a member staged for removal strikes through and offers Undo; a staged
  // add is tagged New and its control unstages it.
  const rosterRows = useMemo<RosterRow[]>(() => {
    const memberRows = members.map((well): RosterRow => {
      const staged = removeUwis.has(well.uwi);

      return {
        id: `member-${well.uwi}`,
        well: wellDisplayName(well),
        source: well.wellId === null ? 'Manual' : 'Existing Well',
        status: wellStatusLabelOrDash(well.status),
        statusToken: well.status ?? '',
        state: staged ? 'removing' : 'member',
        onAction: () => toggleRemove(well.uwi),
      };
    });

    const existingRows = Array.from(addRows.values()).map((row): RosterRow => ({
      id: `add-${row.uwi}`,
      well: wellDisplayName(row),
      source: 'Existing Well',
      status: wellStatusLabelOrDash(row.status),
      statusToken: row.status,
      state: 'adding',
      onAction: () => unstageExistingAdd(row.uwi),
    }));

    const manualRowsForRoster = stagedManual.map((row): RosterRow => ({
      id: `manual-${row.key}`,
      well: manualDisplayName(row),
      source: 'Manual',
      status: wellStatusLabelOrDash(row.status),
      statusToken: row.status,
      state: 'adding',
      onAction: () => patchManualRow(row.key, { include: false }),
    }));

    return [...memberRows, ...existingRows, ...manualRowsForRoster];
  }, [members, addRows, stagedManual, removeUwis]);
  // The manual table's select-all, mirroring the mock: checked when every row is included,
  // indeterminate while only some are.
  const allManualIncluded =
    manualRows.length > 0 && stagedManual.length === manualRows.length;
  const someManualIncluded =
    stagedManual.length > 0 && stagedManual.length < manualRows.length;

  const addCount = addRows.size + stagedManual.length;
  const removeCount = removeUwis.size;
  const hasChanges = addCount > 0 || removeCount > 0;
  // The membership this Save would leave: current members, minus removals, plus adds.
  const resultingCount = members.length - removeCount + addCount;

  // The table's selection is controlled from the map, page by page, exactly as the create modal
  // does it: the header box reads checked when every row on this page is staged, and toggling
  // reconciles only the rows on screen so a pick on another page is never dropped.
  const selection = useMemo<DataTableSelectionState>(() => {
    const onPage = rows.filter((row) => addRows.has(row.id));
    const allOnPage = rows.length > 0 && onPage.length === rows.length;

    return allOnPage
      ? { selectAll: true, toggledNodes: [] }
      : { selectAll: false, toggledNodes: onPage.map((row) => row.id) };
  }, [rows, addRows]);

  /**
   * Stages or unstages the existing wells the catalog table just toggled.
   * @param {DataTableSelectionState} next What the table now has selected.
   */
  const handleSelectionChange = (next: DataTableSelectionState) => {
    const toggled = new Set(next.toggledNodes);
    /**
     * @param {string} uwi A candidate's uwi.
     * @return {boolean} Whether that row is selected.
     */
    const selectedIn = (uwi: string) => {
      return next.selectAll ? !toggled.has(uwi) : toggled.has(uwi);
    };

    setAddRows((prev) => {
      const resolved = new Map(prev);
      rows.forEach((row) => {
        if (selectedIn(row.id)) resolved.set(row.id, row);
        else resolved.delete(row.id);
      });

      return resolved;
    });
  };

  /**
   * Takes one existing well back out of the staged adds.
   * @param {string} uwi The well.
   */
  const unstageExistingAdd = (uwi: string) =>
    setAddRows((prev) => {
      const next = new Map(prev);
      next.delete(uwi);

      return next;
    });

  /**
   * Stages or unstages a removal for a current member.
   * @param {string} uwi The well.
   */
  const toggleRemove = (uwi: string) =>
    setRemoveUwis((prev) => {
      const next = new Set(prev);
      if (next.has(uwi)) next.delete(uwi);
      else next.add(uwi);

      return next;
    });

  /** Appends another blank manual row. */
  const addManualRow = () => {
    setManualRows((prev) => [...prev, emptyManualRow(rowSeq)]);
    setRowSeq((prev) => prev + 1);
  };

  /**
   * Edits one manual row in place.
   * @param {string} key The row's key.
   * @param {Partial<Omit<ManualRow, 'key'>>} patch What changed.
   */
  const patchManualRow = (
    key: string,
    patch: Partial<Omit<ManualRow, 'key'>>
  ) => {
    // Editing the refused row retires its mark — see `refusedRowKey`.
    setEditedRowKeys((edited) =>
      edited.has(key) ? edited : new Set(edited).add(key)
    );
    setManualRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, ...patch } : row))
    );
  };

  /**
   * Removes a manual row entirely.
   * @param {string} key The row's key.
   */
  const dropManualRow = (key: string) =>
    setManualRows((prev) => prev.filter((row) => row.key !== key));

  // Drop every staged change. Used on open, and on a Save that got far enough to land its adds.
  /** Throws away every staged add and removal. */
  const clearStaged = () => {
    setAddRows(new Map());
    setRemoveUwis(new Set());
    setManualRows([]);
  };

  /* What a FAILED Save keeps.
     The adds are one all-or-nothing batch that runs before any removal, so there are two cases
     and they want opposite things:
     - the add POST itself was refused: nothing landed at all, so everything stays staged and the
       reader can correct the row the server named and press Save again;
     - the adds landed and a removal failed: the adds must NOT stay staged, because a manual well
       carries no client key the server could dedupe on and a retry would create a second copy of
       one that is already in. The removals are idempotent by uwi, so the ones that did not go
       through stay, and only those.
     The old behaviour cleared everything either way, which threw away a whole sheet of typing
     over a single invalid date. */
  /**
   * What survives a failed Save.
   * @param {boolean} addsLanded Whether the add batch was accepted before the failure.
   */
  const keepAfterFailure = (addsLanded: boolean) => {
    if (!addsLanded) return;
    setAddRows(new Map());
    setManualRows([]);
  };

  // A row is addable once its status's required dates are present and in order — the same rules
  // the server runs. A dateless stage is always valid. Save is held while any STAGED row is
  // incomplete; a row the reader has unchecked is not going to be sent, so it does not block.
  const manualRowsValid = useMemo(
    () =>
      stagedManual.every(
        (row) => validateManualAddStatus(rowStatusValues(row)).length === 0
      ),
    [stagedManual]
  );

  /**
   * Commits the staged adds and removals.
   * @return {void} Nothing; the outcome arrives through the save callbacks.
   */
  const handleSave = () => {
    setOutcome(null);
    setEditedRowKeys(new Set<string>());
    sentKeysRef.current = stagedManual.map((row) => row.key);

    return save(
      {
        existingUwis: Array.from(addRows.keys()),
        // Name, status and — for a derived status — its required dates, snake-cased. The client
        // row key, the include flag and any date irrelevant to the chosen status are dropped.
        manual: stagedManual.map((row): ManualWellInput => {
          const dates = Object.fromEntries(
            requiredDatesFor(row.status).map((field) => [
              FIELD_TO_SERVER_KEY[field],
              row[field] ?? null,
            ])
          );

          return {
            lease_name: row.leaseName,
            well_number: row.wellNumber,
            status: row.status,
            ...dates,
          };
        }),
        removeUwis: Array.from(removeUwis),
      },
      {
        onSaved: (result) => {
          /* A clean Save closes, as it always has. A partial one — wells the server could not
             find, or removals it refused — keeps the sheet open with what is left still staged,
             because closing would discard work the reader has no way to recover. */
          const partial =
            result.skippedUwis.length > 0 || result.failedRemovals.length > 0;
          if (!partial) {
            clearStaged();
            onClose();
            return;
          }
          setAddRows(new Map());
          setManualRows([]);
          setRemoveUwis(new Set(result.failedRemovals));
          setOutcome(result);
        },
        onError: keepAfterFailure,
      }
    );
  };

  /* Every row the reader has edited since the last Save. The mark means "this is the row that
     was refused", so it retires the moment they change that row — the claim is no longer about
     anything on screen.

     A SET rather than the last-edited key. One key is overwritten by the next row the reader
     touches, so correcting the refused row and then typing in any other row brought the mark
     back on the row they had already fixed — and since `patchManualRow` also runs for the
     include checkbox, one click elsewhere was enough to do it.

     Compared at the row rather than folded into `refusedRowKey`, because deriving a second
     value off that ref read trips `react-hooks/refs` a second time. */
  const [editedRowKeys, setEditedRowKeys] = useState<ReadonlySet<string>>(
    new Set<string>()
  );
  /* Which row the server refused, as a key rather than an index. Null unless the last refusal
     named one — every other refusal is about the batch or the workspace, not a row. */
  const refusedRowKey =
    errorRow === null
      ? null
      : // eslint-disable-next-line react-hooks/refs -- the keys SENT with the last Save, recorded at submit time so the server's row index resolves against what was sent rather than what is on screen now.
        (sentKeysRef.current[errorRow] ?? null);

  /* The close icon and Escape both route here, and neither is a button this sheet can disable —
     Carbon's ComposedModal honours a `false` return by leaving the modal open (the typing says
     `() => void`, hence the cast). Without this, Escape mid-save unmounts the body while the
     request is still in flight: the save goes on, whatever it reports lands on a sheet nobody
     can see, and the reader is left with no sheet and no message.
     Not `preventCloseOnClickOutside` — that is not a Tearsheet prop, the shell overwrites it,
     and with two actions this sheet already refuses an outside click. */
  const guardedClose = (() => {
    if (saving) return false;
    onClose();

    return undefined;
  }) as () => void;

  // The button names BOTH halves of the edit, so a sheet with one add and two removals does not
  // claim to only be adding.
  const saveLabel = useMemo(() => {
    if (saving) return 'Saving…';
    if (addCount > 0 && removeCount > 0) {
      return `Add ${addCount}, Remove ${removeCount}`;
    }
    if (removeCount > 0) {
      return `Remove ${removeCount} ${removeCount === 1 ? 'Well' : 'Wells'}`;
    }
    if (addCount > 0) {
      return `Add ${addCount} ${addCount === 1 ? 'Well' : 'Wells'}`;
    }

    return 'Add Wells';
  }, [saving, addCount, removeCount]);

  const loadError = candidatesResult.isError
    ? 'Failed to load wells for this search'
    : null;

  return (
    <Tearsheet
      open={open}
      onClose={guardedClose}
      className={styles.tearsheet}
      label={workspace.name}
      title="Add or Remove Wells"
      hasCloseIcon
      closeIconDescription="Close"
      actions={[
        {
          key: 'cancel',
          kind: 'ghost',
          label: 'Cancel',
          disabled: saving,
          onClick: onClose,
        },
        {
          key: 'save',
          kind: 'primary',
          label: saveLabel,
          disabled: saving || !hasChanges || !manualRowsValid,
          onClick: handleSave,
        },
      ]}
    >
      {open && (
        <div className={styles.body}>
          {(error || loadError) && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Error"
              subtitle={error ?? loadError ?? ''}
              className={styles.banner}
            />
          )}

          {outcome && (
            <InlineNotification
              kind="warning"
              lowContrast
              title="Partly saved"
              subtitle={partialSummary(outcome)}
              className={styles.banner}
            />
          )}

          <div className={styles.layout}>
            <section className={styles.addCol}>
              <div className={styles.addHead}>
                <h3 className={styles.colTitle}>Add Wells</h3>
                <div className={styles.addHeadActions}>
                  <Button
                    hasIconOnly
                    kind="ghost"
                    size="sm"
                    iconDescription="Expand all"
                    tooltipPosition="left"
                    renderIcon={ExpandAll}
                    disabled={existingOpen && manualOpen}
                    onClick={() => {
                      setExistingOpen(true);
                      setManualOpen(true);
                    }}
                  />
                  <Button
                    hasIconOnly
                    kind="ghost"
                    size="sm"
                    iconDescription="Collapse all"
                    tooltipPosition="left"
                    renderIcon={CollapseAll}
                    disabled={!existingOpen && !manualOpen}
                    onClick={() => {
                      setExistingOpen(false);
                      setManualOpen(false);
                    }}
                  />
                </div>
              </div>
              <Accordion>
                <AccordionItem
                  open={existingOpen}
                  onHeadingClick={({ isOpen }) => setExistingOpen(isOpen)}
                  title={`From Existing Database (${addRows.size})`}
                >
                  <p className={styles.intro}>
                    Search every well not already in this workspace, and check
                    the ones to add.
                  </p>
                  {candidatesResult.isLoading ? (
                    <InlineLoading description="Loading wells…" />
                  ) : (
                    <div className={styles.tableArea}>
                      <DataTable<CandidateRow>
                        data={rows}
                        columns={COLUMNS}
                        getRowId={(row) => row.id}
                        initialTableState={{
                          page: 1,
                          pageSize: DEFAULT_PAGE_SIZE,
                        }}
                        scroll={{
                          mode: 'paginated',
                          serverDriven: true,
                          totalCount,
                        }}
                        controlledState={{ selection }}
                        onSelectionChange={(state) =>
                          handleSelectionChange(state.selection)
                        }
                        onPaginationChange={(state) => {
                          setPage(state.page);
                          setPageSize(state.pageSize);
                        }}
                        onGlobalSearchChange={(state) => {
                          setSearch(state.globalFilter.trim());
                          setPage(1);
                        }}
                        searchPlaceholder="Search by name, UWI, or operator"
                        // The mock's toolbar: the column-visibility menu, the inline column
                        // filters and the reset beside the search. All three are the shared
                        // DataTable's own, so they filter and reset for real rather than
                        // decorating the header. Reordering/resizing/pinning and export stay
                        // off — this is a picker, not a report.
                        config={{
                          selectionType: 'multi',
                          sorting: false,
                          globalSearch: true,
                          columnFilters: true,
                          columnVisibility: true,
                          columnReordering: false,
                          columnResizing: false,
                          columnPinning: false,
                          export: false,
                        }}
                      />
                    </div>
                  )}
                </AccordionItem>

                <AccordionItem
                  open={manualOpen}
                  onHeadingClick={({ isOpen }) => setManualOpen(isOpen)}
                  title={`From Manual (${stagedManual.length})`}
                >
                  <div className={styles.manualHeader}>
                    <span className={styles.rowCount}>
                      {manualRows.length}{' '}
                      {manualRows.length === 1 ? 'row' : 'rows'}
                      {stagedManual.length > 0 && (
                        <span className={styles.pickedCount}>
                          {' · '}
                          {stagedManual.length} selected
                        </span>
                      )}
                    </span>
                    <Button
                      kind="tertiary"
                      size="sm"
                      renderIcon={Add}
                      onClick={addManualRow}
                    >
                      Add row
                    </Button>
                  </div>
                  {manualRows.length === 0 ? (
                    <p className={styles.empty}>
                      No rows yet. Add one for a well that is neither in
                      inventory nor on a document.
                    </p>
                  ) : (
                    <div className={styles.manualTableWrap}>
                      <table className={styles.manualTable}>
                        <thead>
                          <tr>
                            <th scope="col" className={styles.manualCheckCell}>
                              {/* The mock's select-all. "Included" is what the roster
                                shows and what Save sends, so unchecking here is how a
                                typed row is held back without losing it. */}
                              <Checkbox
                                id="manual-include-all"
                                labelText="Include all rows"
                                hideLabel
                                checked={allManualIncluded}
                                indeterminate={someManualIncluded}
                                disabled={manualRows.length === 0}
                                onChange={(_e, { checked }) =>
                                  setManualRows((prev) =>
                                    prev.map((r) => ({
                                      ...r,
                                      include: checked,
                                    }))
                                  )
                                }
                              />
                            </th>
                            <th scope="col">Well Name</th>
                            <th scope="col">Well Number</th>
                            <th scope="col">County</th>
                            <th scope="col">State</th>
                            <th scope="col">Formation</th>
                            <th scope="col">Status</th>
                            <th scope="col" aria-label="Action" />
                          </tr>
                        </thead>
                        <tbody>
                          {/* eslint-disable-next-line react-hooks/refs -- reads the sent-keys ref captured above, for the same reason. */}
                          {manualRows.map((row, index) => {
                            /* Every row renders the same fields, so each hidden label carries
                               its row number — otherwise a screen reader hears "Well name"
                               four times with nothing to tell them apart, and a test cannot
                               address a second row at all. */
                            const rowNumber = index + 1;
                            const dateFields = requiredDatesFor(row.status);
                            const rowErrors = row.include
                              ? validateManualAddStatus(rowStatusValues(row))
                              : [];
                            // The server refused THIS row — mark it, so a batch refusal does
                            // not leave the reader comparing a message against every row.
                            const refused =
                              row.key === refusedRowKey &&
                              !editedRowKeys.has(row.key);

                            return (
                              <Fragment key={row.key}>
                                <tr
                                  className={
                                    row.include ? undefined : styles.rowExcluded
                                  }
                                >
                                  <td className={styles.manualCheckCell}>
                                    <Checkbox
                                      id={`manual-include-${row.key}`}
                                      labelText={`Include row ${rowNumber}`}
                                      hideLabel
                                      checked={row.include}
                                      onChange={(_e, { checked }) =>
                                        patchManualRow(row.key, {
                                          include: checked,
                                        })
                                      }
                                    />
                                  </td>
                                  <td>
                                    <TextInput
                                      id={`manual-lease-${row.key}`}
                                      labelText={`Well name, row ${rowNumber}`}
                                      hideLabel
                                      size="sm"
                                      placeholder="Well name"
                                      invalid={refused}
                                      invalidText="The server refused this row — see the message above."
                                      value={row.leaseName}
                                      onChange={(e) =>
                                        patchManualRow(row.key, {
                                          leaseName: e.target.value,
                                        })
                                      }
                                    />
                                  </td>
                                  <td>
                                    <TextInput
                                      id={`manual-number-${row.key}`}
                                      labelText={`Well number, row ${rowNumber}`}
                                      hideLabel
                                      size="sm"
                                      placeholder="1H"
                                      value={row.wellNumber}
                                      onChange={(e) =>
                                        patchManualRow(row.key, {
                                          wellNumber: e.target.value,
                                        })
                                      }
                                    />
                                  </td>
                                  {/*
                                    County, State and Formation are the mock's columns, shown as
                                    DERIVED placeholders rather than inputs: the backend DERIVES state and county from the
                                    surface hole via PostGIS once a wellbore is built
                                    (WellWorkspace::GeographicRecalc), and srp_landing_zone is
                                    deferred pending INS-12661. They are also absent from
                                    StagedWell::MANUAL_WELL_FIELDS, so a typed value would be
                                    dropped by strong params and then overwritten by the recalc.
                                    Shown as the derived placeholders they are, rather than as
                                    inputs that would quietly discard what was typed into them.
                                  */}
                                  <td
                                    className={styles.derivedCell}
                                    title={DERIVED_HINT}
                                  >
                                    —
                                  </td>
                                  <td
                                    className={styles.derivedCell}
                                    title={DERIVED_HINT}
                                  >
                                    —
                                  </td>
                                  <td
                                    className={styles.derivedCell}
                                    title={DERIVED_HINT}
                                  >
                                    —
                                  </td>
                                  <td>
                                    {/*
                                      Dropdown, not Select: Select renders a native
                                      <select>, so the options come up as the OS menu
                                      rather than a Carbon list box. autoAlign floats the
                                      menu out of this scrolling cell, which clips it.
                                    */}
                                    <Dropdown<WellStatusToken>
                                      id={`manual-status-${row.key}`}
                                      titleText={`Status, row ${rowNumber}`}
                                      label="Status"
                                      hideLabel
                                      size="sm"
                                      autoAlign
                                      items={WELL_STATUS_ORDER}
                                      selectedItem={row.status}
                                      itemToString={(item) => {
                                        return item
                                          ? WELL_STATUS_LABELS[item]
                                          : '';
                                      }}
                                      onChange={({ selectedItem }) => {
                                        if (selectedItem) {
                                          patchManualRow(row.key, {
                                            status: selectedItem,
                                          });
                                        }
                                      }}
                                    />
                                  </td>
                                  <td className={styles.manualActionCell}>
                                    <Button
                                      hasIconOnly
                                      kind="danger--ghost"
                                      size="sm"
                                      iconDescription={`Remove row ${rowNumber}`}
                                      tooltipPosition="left"
                                      renderIcon={SubtractAlt}
                                      onClick={() => dropManualRow(row.key)}
                                    />
                                  </td>
                                </tr>
                                {dateFields.length > 0 && (
                                  <tr
                                    className={
                                      row.include
                                        ? undefined
                                        : styles.rowExcluded
                                    }
                                  >
                                    {/* The status's required dates, on their own line
                                      under the row they belong to — a nested row rather
                                      than extra columns, since which dates appear
                                      changes per row. */}
                                    <td />
                                    <td colSpan={7}>
                                      <div className={styles.manualDates}>
                                        {dateFields.map((field) => {
                                          const message = errorFor(
                                            rowErrors,
                                            field
                                          );

                                          return (
                                            <DatePicker
                                              key={field}
                                              id={`manual-${field}-${row.key}`}
                                              labelText={`${DATE_FIELD_LABELS[field]}, row ${rowNumber}`}
                                              value={row[field] ?? undefined}
                                              size="sm"
                                              invalid={Boolean(message)}
                                              invalidText={message ?? ''}
                                              // flatpickr hands back an array; an empty
                                              // one is a cleared field, which must null
                                              // the value rather than leave the previous
                                              // date standing.
                                              onChange={(dates: Date[]) =>
                                                patchManualRow(row.key, {
                                                  [field]: dates[0]
                                                    ? getDateString(dates[0])
                                                    : null,
                                                })
                                              }
                                            />
                                          );
                                        })}
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </AccordionItem>
              </Accordion>
            </section>

            <section className={styles.wsCol}>
              <h3 className={styles.colTitle}>
                Wells in this Workspace (
                <FormattedCount value={resultingCount} />)
              </h3>
              {rosterRows.length === 0 ? (
                <p className={styles.empty}>
                  No wells staged — add existing or new wells to build the
                  workspace.
                </p>
              ) : (
                <div className={styles.tableArea}>
                  <DataTable<RosterRow>
                    data={rosterRows}
                    columns={ROSTER_COLUMNS}
                    getRowId={(row) => row.id}
                    searchPlaceholder="Search this workspace…"
                    // The same toolbar the catalog carries — search, the column-visibility
                    // menu, the inline filters and the reset — and all four work on this list
                    // for real. No pagination: a workspace's membership is short, and `scroll`
                    // omitted renders every row.
                    config={{
                      sorting: false,
                      globalSearch: true,
                      columnFilters: true,
                      columnVisibility: true,
                      columnReordering: false,
                      columnResizing: false,
                      columnPinning: false,
                      export: false,
                    }}
                  />
                </div>
              )}
            </section>
          </div>
        </div>
      )}
    </Tearsheet>
  );
}
